import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import { trackActivityApi } from "../services/activityLog";

// Maps URL path prefix → human-readable module name
const PATH_MODULE_MAP = [
  ["/user/user-detail",             "User Detail"],
  ["/user/delete",                  "User Management"],
  ["/user",                         "User Management"],
  ["/consultant-management/consultant-detail", "Consultant Detail"],
  ["/consultant-management/add",    "Add Consultant"],
  ["/consultant-management/edit",   "Edit Consultant"],
  ["/consultant-management/check-performance", "Consultant Performance"],
  ["/consultant-management/kpi",    "Consultant KPI"],
  ["/consultant-management",        "Consultant Management"],
  ["/consultant-performance-alerts", "Performance Alerts"],
  ["/operations-ecosystem",         "Operations Ecosystem"],
  ["/power-customer-return",        "Power Customer Return Reminders"],
  ["/consultant-performance",       "Consultant Performance"],
  ["/consultant-payout",            "Consultant Payout"],
  ["/dashboard-matrix-analytics",   "Dashboard Matrix"],
  ["/enhanced-dashboard",           "Enhanced Dashboard"],
  ["/analytics-dashboard",          "Analytics Dashboard"],
  ["/claude-data-analysis",         "Claude Data Analysis"],
  ["/dashboard",                    "Dashboard"],
  ["/dashboard-marketing",          "DASHBOARD"],
  ["/daily-consult-performance",    "Daily Consult Performance"],
  ["/waitlist-management",          "Waitlist Management"],
  ["/booked-waitlist",              "Booked Waitlist"],
  ["/failed-consultation",          "Failed Consultation"],
  ["/missed-call-chat",             "Missed Call/Chat"],
  ["/completed-call-chat",          "Completed Consultation"],
  ["/completed-queries",            "Completed Queries"],
  ["/progress-call-chat",           "In-Progress Consultation"],
  ["/online-offline-consultant",    "Online/Offline Consultants"],
  ["/flag-request",                 "Flag Requests"],
  ["/transaction-management",       "Transaction Management"],
  ["/notification-management/add",  "Add Notification"],
  ["/notification-management",      "Notification Management"],
  ["/support-management/query-masters", "Support Query Masters"],
  ["/support-management",           "Support Management"],
  ["/feedback-management",          "Feedback Management"],
  ["/banner",                       "Banner Management"],
  ["/content-management",           "Content Management"],
  ["/music-category",               "Music Management"],
  ["/meditation-category",          "Meditation"],
  ["/meditation-dashboard",         "Meditation Dashboard"],
  ["/meditation",                   "Meditation"],
  ["/training-category",            "Training Management"],
  ["/category",                     "Category Management"],
  ["/image-management",             "Image Management"],
  ["/gallery-management",           "Gallery Management"],
  ["/vastu-compass-category",       "Vastu Compass"],
  ["/gift-management",              "Gift Management"],
  ["/segments/builder",             "Segment Builder"],
  ["/segments/detail",              "Segment Detail"],
  ["/segments",                     "Segment Management"],
  ["/community-management",         "Community Management"],
  ["/explore-management",           "Explore Management"],
  ["/explore",                      "Explore Management"],
  ["/refer-management",             "Refer & Earn"],
  ["/request-management",           "Request Management"],
  ["/discount-management",          "Discount Management"],
  ["/horoscope-management",         "Horoscope Management"],
  ["/live-management",              "Live Management"],
  ["/utm-management",               "UTM Management"],
  ["/playstore-screenshot",         "Playstore Screenshots"],
  ["/activity-log",                 "Activity Log"],
  ["/settings",                     "Settings"],
  ["/zodiac-message",               "Zodiac Messages"],
  ["/common-message",               "Common Message"],
  ["/concern-management",           "Concern & Remedy"],
];

function resolveModule(pathname) {
  const lower = pathname.toLowerCase();
  for (const [prefix, name] of PATH_MODULE_MAP) {
    if (lower.startsWith(prefix)) return name;
  }
  // Fallback: convert path to readable name
  const seg = pathname.replace(/^\//, "").split("/")[0];
  return seg ? seg.replace(/-/g, " ").replace(/\b\w/g, c => c.toUpperCase()) : "Admin Panel";
}

// Module-level dedup — persists across component remounts.
// Prevents duplicate logs when SidebarLayout remounts due to React re-renders.
const _recentlyLogged = new Map(); // path -> timestamp
const DEDUP_MS = 60 * 1000; // don't re-log the same path within 60 seconds

/**
 * Placed inside SidebarLayout — auto-logs a VIEW event on every route change.
 * No setup needed on individual pages.
 */
const useNavigationTracker = () => {
  const location = useLocation();

  useEffect(() => {
    const token = localStorage.getItem("vedic_token");
    if (!token) return;

    const path = location.pathname;

    // Don't log login/auth pages
    if (path === "/" || path.startsWith("/forgot") || path.startsWith("/reset") || path.startsWith("/password-reset")) return;

    // Dedup: skip if same path was logged recently
    const now = Date.now();
    const lastLogged = _recentlyLogged.get(path) || 0;
    if (now - lastLogged < DEDUP_MS) return;
    _recentlyLogged.set(path, now);

    const email  = localStorage.getItem("vedic_email") || "Admin";
    const module = resolveModule(path);

    trackActivityApi({
      action: "VIEW",
      module,
      description: `${email} viewed ${module}`,
      resourceId: "",
      resourceType: "",
      metadata: { path },
    }).catch(() => {});
  }, [location.pathname]);
};

export default useNavigationTracker;
